/**
 * the schema plugin module
 * @module component/edm-core/schema-plugin
 */
import _ from 'underscore';
import {
  hasOwnProperty,
  defineConstProperty,
} from '@bingads-webui/reflection';
import { Registry } from '@bingads-webui/registry';

import typesPlugin from './types-plugin';

function getSchemaName(schema) {
  const { $$ODataExtension, name } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Name;
  }

  // deprecated
  // There is no such keyword as "name" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return name;
}

function getSchemaBaseType(schema) {
  const { $$ODataExtension, clrTypeBase } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.BaseType;
  }

  // deprecated
  // There is no such keyword as "clrTypeBase" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return clrTypeBase;
}

function getSchemaKey(schema) {
  const { $$ODataExtension, key } = schema;
  if ($$ODataExtension) {
    return $$ODataExtension.Key;
  }

  // deprecated
  // There is no such keyword as "key" in JSON Schema, it's specific to OData
  // See:
  //  http://json-schema.org/latest/json-schema-core.html
  //  http://json-schema.org/latest/json-schema-validation.html
  return key;
}

export default (edm, {
  schemas = {},
  // TODO: deprecated, every schemas should be able to have different namespaces
  // we are still using it with legacy schemas
  namespace = null,
} = {}) => {
  if (hasOwnProperty(edm, 'schema')) {
    return;
  }

  typesPlugin(edm);

  const {
    CollectionType,
    EntityType,
    PrimitiveType,
    ComplexType,
    OneOfType,
  } = edm.types;

  defineConstProperty(edm, 'schema', (() => {
    function getSchemaFullName(schema) {
      return schema.$ref ?
        schema.$ref.replace(/\//g, '.') :
        Registry.getQualifiedName(getSchemaName(schema), namespace);
    }

    /**
     * Get type name object with its information
     * @param {Schema[]} dependencies - An output paramter, a queue of dependent schemas
     * @param {Object} typeInfo - The schema information of the property
     * @return {String} The type name
     */
    function getTypeName(dependencies, typeInfo) {
      let schema = null;
      let typeName = null;

      if (typeInfo.type === 'array') {
        return CollectionType.collectionTypeName(getTypeName(dependencies, typeInfo.items));
      } else if (typeInfo.oneOf) {
        return OneOfType.oneOfTypeName(typeInfo.oneOf.map(item => getTypeName(dependencies, item)));
      } else if (typeInfo.type === 'object') {
        ({ schema } = typeInfo);
        // it's possible for the schema to be undefined, use 'object' type by default
        typeName = getSchemaFullName(schema || typeInfo);
      } else {
        // primitive types
        typeName = typeInfo.type;
      }

      if (schema) {
        dependencies.push(schema);
      }

      return typeName;
    }

    /**
     * Define an entity type, and its dependencies
     *
     * @param {Schema} schema - The schema for the entity type
     * @return {Void} Nothing to return
     */
    function defineSchemaType(schema) {
      const qualifiedName = getSchemaFullName(schema);

      if (!edm.types.resolve(qualifiedName)) {
        const dependencies = [];
        const properties = _.chain(schema.properties)
          .map((typeInfo, name) => [
            name,
            { typeName: getTypeName(dependencies, typeInfo, name) },
          ])
          .object()
          .value();

        const type = new EntityType({
          name: qualifiedName,
          baseTypeName: getSchemaBaseType(schema) || 'object',
          key: getSchemaKey(schema),
          properties,
        });

        defineConstProperty(type, 'schema', schema);

        _.each(dependencies, defineSchemaType);
      }
    }

    // Define all the primitive types
    _.each({
      string: String,
      integer: Number,
      number: Number,
      datetime: Date,
      boolean: Boolean,
      null: null,
    }, (jsType, name) => new PrimitiveType({ name, jsType }));
    // TODO: support `oneOf` in schema

    // Define the base object type
    /* eslint no-new: 0 */
    new ComplexType({
      name: 'object',
      properties: {},
    });

    _.each(schemas, defineSchemaType);

    return _.chain({})
      .defineConstProperty('schemas', schemas)
      .defineConstProperty('namespace', namespace)
      .value();
  })());
};
